import React, { FC, Fragment, lazy, Suspense } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';

import { camelCasify } from '../../utils/navigation';
import i18n from '../../i18n';
import { LoadingIndicator } from '../../components/loadingIndicator';
import Layout from '../../components/layout';
import { ProtectedRoute } from '../protectedRoute';
import { BuildingsTabContent } from '../main/tabs/buildingsTab';
import { OtherBuildingsTabContent } from '../main/tabs/otherBuildingsTab';
import { SearchTabContent } from '../main/tabs/searchTab';
import { ExpertRegistrationTab } from '../main/tabs/expertRegistrationTab';
import MainScreenLayout from '../main';
import { Solceller } from '../../components/registering/steps/solceller';
import { Ventilasjon } from '../../components/registering/steps/ventilasjon';

import { BuildingRoutes } from './buildingRoutes';
import { FeatureTestRoutes } from './featureTestRoutes';

const HomeScreen = lazy(() => import('../home'));
const LoginScreen = lazy(() => import('../login'));
const LogoutScreen = lazy(() => import('../logout'));
const ProfileScreen = lazy(() => import('../profile'));
const DirectLogin = lazy(() => import('../directLogin'));
const PageNotFound = lazy(() => import('../pageNotFound'));
const StatisticsScreen = lazy(() => import('../statistics'));
const StatisticsScreenOld = lazy(() => import('../statistics/indexOld'));
const CreateBuilding = lazy(() => import('../createBuilding'));
const ExpertToolsXMLPortfolioScreen = lazy(() => import('../expertToolsXMLPortfolio'));
const EnhetXmlRegistration = lazy(() => import('../expertRegistration/enhet/enhetXmlRegistration'));
const EnhetJsonRegistration = lazy(() => import('../expertRegistration/enhet/enhetJsonRegistration'));
const FlerboligXmlRegistration = lazy(() => import('../expertRegistration/flerbolig/flerboligXmlRegistration'));

const FlerboligJsonRegistration = lazy(() => import('../expertRegistration/flerbolig/flerboligJsonRegistration'));

export const AppRoutes: FC = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const defaultTitle = [t('companyName'), t('appName')].join(' ');
  const documentTitle = pathname
    .split('/')
    .reverse()
    .reduce((acc: string[], cur) => (cur ? [...acc, t(camelCasify(cur))] : acc), [])
    .join(' | ');

  return (
    <Fragment>
      <Helmet
        defaultTitle={defaultTitle}
        htmlAttributes={{ lang: i18n.language }}
        title={documentTitle}
        titleTemplate={`%s | ${defaultTitle}`}
      />
      <Layout fullWidth>
        <Suspense fallback={<LoadingIndicator text={t('loadingContent')} />}>
          <Routes>
            <Route element={<FeatureTestRoutes />}>
              {/* Feature testing enabled by energimerkeV2025Enabled feature flag  */}
              <Route path="solceller" element={<Solceller />} />
              <Route path="ventilasjon" element={<Ventilasjon />} />
            </Route>
            <Route index element={<HomeScreen />} />
            <Route path="login" element={<LoginScreen />} />
            <Route path="logout" element={<LogoutScreen />} />
            <Route path="direct-login" element={<DirectLogin />} />
            <Route path="statistikk" element={<StatisticsScreen />} />
            <Route path="statistikk-old/*" element={<StatisticsScreenOld />} />
            {/* Routes that require authentication */}
            <Route element={<ProtectedRoute />}>
              <Route path="bygg">
                <Route path="*" element={<BuildingRoutes />} />
              </Route>
              <Route path="profil" element={<ProfileScreen />} />
              <Route path="opprett-bygg" element={<CreateBuilding />} />

              <Route element={<MainScreenLayout />}>
                <Route path="ekspertregistrering" element={<ExpertRegistrationTab />} />
                <Route path="boliger" element={<BuildingsTabContent />} />
                <Route path="sok" element={<SearchTabContent />}>
                  <Route path="*" />
                </Route>
                <Route path="andres-boliger" element={<OtherBuildingsTabContent />} />
              </Route>

              <Route path="ekspertregistrering-portefolje/*" element={<ExpertToolsXMLPortfolioScreen />} />

              <Route path="ekspertregistrering">
                <Route path="xml">
                  <Route path="enhet/:step?/:registrationId?" element={<EnhetXmlRegistration />} />
                  <Route path="flerbolig/:step?/:flerboligId?" element={<FlerboligXmlRegistration />} />
                </Route>
                <Route path="json">
                  <Route path="enhet/:step?/:registrationId?" element={<EnhetJsonRegistration />} />
                  <Route path="flerbolig/:step?/:flerboligId?" element={<FlerboligJsonRegistration />} />
                </Route>
              </Route>
            </Route>
            <Route path="*" element={<PageNotFound />} />
          </Routes>
        </Suspense>
      </Layout>
    </Fragment>
  );
};
